/**
 * useAssistant — Manages assistant/retrieval selection and configuration.
 *
 * Owns: retrieval list, selected assistant, assistant config (colors, logo,
 * name, capabilities like streaming/matrix/audio).
 */

import { useState, useEffect, useRef, useCallback } from "react";
import { getRetrievalsInfo } from "../api/RetrievalsApi";

const parseColors = (colors) => {
  if (!colors) return null;
  return {
    nameColor: colors.nameColor || "",
    borderColor: colors.borderColor || "",
    primaryColor: colors.primaryColor || "",
    secondaryColor: colors.secondaryColor || "",
  };
};

export const useAssistant = ({ retrievalId, getRetrieval, onError, onNavigate }) => {

  // Retrieval list
  const [retrievals, setRetrievals] = useState([]);
  const [selectedRetrieval, setSelectedRetrieval] = useState(null);
  const [currentRetrievalDescription, setCurrentRetrievalDescription] =
    useState("");

  // Assistant config
  const [loading, setLoading] = useState(!retrievalId);
  const [allowAsk, setAllowAsk] = useState(false);
  const [iniMessage, setIniMessage] = useState(null);
  const [maxMsgLength, setMaxMsgLength] = useState(1000);
  const [chatName, setChatName] = useState("");
  const [urlLogo, setUrlLogo] = useState("");
  const [assistantColors, setAssistantColors] = useState(null);

  // Capabilities
  const [streamingAvailable, setStreamingAvailable] = useState(false);
  const [asyncAvailable, setAsyncAvailable] = useState(false);
  const [matrixModeAvailable, setMatrixModeAvailable] = useState(false);
  const [audioAvailable, setAudioAvailable] = useState(false);
  const [attachmentConfig, setAttachmentConfig] = useState(null);
  const [conversationStarters, setConversationStarters] = useState([]);

  // Guards
  const lastFetchedRetrievalIdRef = useRef(null);
  const retrievalsFetchedRef = useRef(false);
  const retrievalsRef = useRef([]);

  // Apply assistant data from API response
  const applyAssistantData = useCallback((data) => {
    if (data.initial_message !== undefined) setIniMessage(data.initial_message);
    if (data.max_msg_length !== undefined) setMaxMsgLength(data.max_msg_length);
    if (data.streaming_available !== undefined)
      setStreamingAvailable(data.streaming_available);
    if (data.async_available !== undefined)
      setAsyncAvailable(data.async_available);
    if (data.matrix_mode_available !== undefined)
      setMatrixModeAvailable(data.matrix_mode_available);
    if (data.realtime_available !== undefined)
      setAudioAvailable(data.realtime_available);
    if (data.attachment_config !== undefined)
      setAttachmentConfig(data.attachment_config);
    // Always assigned (not guarded on undefined) so switching to an agent without starters clears
    // the previous agent's ones. The feature flag gates them here, so the UI just reads a list.
    setConversationStarters(
      data.conversation_starters_enabled && Array.isArray(data.conversation_starters)
        ? data.conversation_starters
        : [],
    );
    setChatName(data.info || "");
    setUrlLogo(data.logo || "");
    setAssistantColors(data.colors ? parseColors(data.colors) : null);
    if (data.description) setCurrentRetrievalDescription(data.description);
  }, []);

  // Reset assistant-specific visual state (called on agent switch / new chat)
  const resetAssistantVisuals = useCallback(() => {
    setChatName("");
    setUrlLogo("");
    setAssistantColors(null);
  }, []);

  // Fetch individual retrieval info + fetch retrieval list
  // No cleanup/cancelled flag: the guard refs already prevent duplicate fetches,
  // and in React 18 state updates on unmounted components are safe no-ops.
  useEffect(() => {
    const pathMatch = window.location.pathname.match(/\/projects\/([^/]+)/);
    const assistantIdFromPath = pathMatch ? pathMatch[1] : null;
    const currentRetrievalId = retrievalId || assistantIdFromPath;

    if (currentRetrievalId) {
      setSelectedRetrieval(currentRetrievalId);
    }

    if (
      currentRetrievalId &&
      currentRetrievalId !== lastFetchedRetrievalIdRef.current
    ) {
      lastFetchedRetrievalIdRef.current = currentRetrievalId;

      const fetchRetrieval = async () => {
        try {
          const data = await getRetrieval(currentRetrievalId);
          applyAssistantData(data);
          setAllowAsk(true);
          setLoading(false);
        } catch (error) {
          if (onError) onError(null, error?.customMessage);
          console.log(error);
          setLoading(false);
        }
      };
      fetchRetrieval();
    } else if (!currentRetrievalId) {
      setAllowAsk(false);
      setLoading(false);
    }

    // Fetch full retrieval list (one-shot)
    const fetchRetrievals = async () => {
      if (retrievalsFetchedRef.current) return;
      retrievalsFetchedRef.current = true;

      try {
        const data = await getRetrievalsInfo();
        setRetrievals(data);
        retrievalsRef.current = data;

        const effectiveId = retrievalId || assistantIdFromPath;
        if (effectiveId) {
          const found = data.find((r) => r.id === effectiveId);
          if (found) {
            setSelectedRetrieval(effectiveId);
            applyAssistantData(found);
            setAllowAsk(true);
          } else {
            // Not in list, fetch directly
            try {
              const retrieval = await getRetrieval(effectiveId);
              if (retrieval) {
                applyAssistantData(retrieval);
                setAllowAsk(true);
              }
            } catch (error) {
              console.error("Error fetching retrieval directly:", error);
            }
          }
        }
        setLoading(false);
      } catch (error) {
        console.log(error);
        setLoading(false);
      }
    };

    fetchRetrievals();
  }, [
    retrievalId,
    getRetrieval,
    applyAssistantData,
    onError,
  ]);

  // Set CSS custom property for border color
  useEffect(() => {
    document.documentElement.style.setProperty(
      "--assistant-border-color",
      assistantColors?.borderColor || "black",
    );
  }, [assistantColors]);

  // Handle retrieval selection change
  const handleRetrievalChange = useCallback(
    async (value) => {
      setLoading(true);
      setSelectedRetrieval(value);
      setIniMessage(null);
      // Reset visuals before applying new agent data
      resetAssistantVisuals();

      const selected = retrievals.find((r) => r.id === value);
      if (selected) {
        applyAssistantData(selected);
        setAllowAsk(true);
        setLoading(false);
      } else {
        // Not in cached list, fetch directly
        try {
          const data = await getRetrieval(value);
          applyAssistantData(data);
          setAllowAsk(true);
        } catch (error) {
          console.error("Error fetching retrieval:", error);
          if (onError) onError(null, error?.customMessage);
        } finally {
          setLoading(false);
        }
      }

      lastFetchedRetrievalIdRef.current = value;
      if (onNavigate) onNavigate(value);
    },
    [retrievals, onNavigate, applyAssistantData, resetAssistantVisuals, getRetrieval, onError],
  );

  // Get cached assistant data (for startNewChat without network call)
  const getCachedAssistant = useCallback(
    (id) => retrievalsRef.current.find((r) => r.id === id),
    [],
  );

  return {
    // State
    retrievals,
    selectedRetrieval,
    currentRetrievalDescription,
    loading,
    setLoading,
    allowAsk,
    setAllowAsk,
    iniMessage,
    setIniMessage,
    maxMsgLength,
    setMaxMsgLength,
    chatName,
    setChatName,
    urlLogo,
    setUrlLogo,
    assistantColors,
    setAssistantColors,
    streamingAvailable,
    asyncAvailable,
    matrixModeAvailable,
    audioAvailable,
    attachmentConfig,
    conversationStarters,

    // Handlers
    handleRetrievalChange,
    resetAssistantVisuals,
    applyAssistantData,
    getCachedAssistant,
  };
};
